import React, { useState, useRef, useEffect, MouseEventHandler, useCallback, PropsWithChildren } from 'react';
import { useSelected, useFocused, useSlateStatic, ReactEditor } from 'slate-react';
import { Transforms } from 'slate';
import { Button } from 'components/_new/Button';
import { classNames } from 'primereact/utils';
import { OverlayElement } from 'components/_new/Overlay';
import { ButtonGroup } from 'components/_new/ButtonGroup/ButtonGroup';
import {
	CustomEditor,
	EmbedBlockFormat,
	EmbedSpecialAttributes,
	ImageElement,
	VideoElement,
} from 'components/_new/NewSlateEditor/types';
import useResize from '../../utils/customHooks/useResize';
import { EmbedEditPopup } from './EmbeEditPopup';
import { EmbedEditFormData } from './EmbedEditForm';

type EmbedElementProp = PropsWithChildren<{
	attributes: Record<string, string>;
	element: ImageElement | VideoElement;
	aspectRatio?: number;
}>;

const EMBED_TYPES = Object.values(EmbedBlockFormat);

export const EmbedElement = ({ attributes, element, aspectRatio, children }: EmbedElementProp) => {
	const { url, alt, widthUnit } = element;

	const selected = useSelected();
	const focused = useFocused();
	const editor = useSlateStatic();

	const editPopupRef = useRef<OverlayElement>(null);

	const [isFullWidth, setIsFullWidth] = useState(element.width === 'full');
	const [float, setFloat] = useState<'left' | 'right' | undefined>(element.float);
	const [unit, setUnit] = useState<'px' | '%'>(widthUnit || 'px');
	const [editorWidth, setEditorWidth] = useState<number | null>(null);

	const { size, onMouseDown, finalSize, resizing } = useResize(
		isFullWidth ? {} : { aspectRatio, minWidth: 100, maxWidth: 800 }
	);

	const updateEditorWidth = useCallback((newWidth: number) => {
		if (newWidth !== editorWidth) {
			setEditorWidth(newWidth);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	useEffect(() => {
		const observer = new ResizeObserver((entries) => {
			// eslint-disable-next-line no-restricted-syntax
			for (const entry of entries) {
				if (entry.contentRect) {
					updateEditorWidth(entry.contentRect.width);
				}
			}
		});
		if (editor.container) {
			observer.observe(editor.container);
		}
		return () => observer.disconnect();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editor.container]);
	const percentSize = unit === '%' && editorWidth ? Math.ceil((size.width / editorWidth) * 100) : undefined;

	useEffect(() => {
		let width: EmbedSpecialAttributes['width'];
		if (unit === 'px') {
			width = finalSize.width;
		} else {
			width = isFullWidth ? 100 : percentSize;
		}
		Transforms.setNodes<ImageElement | VideoElement>(
			editor,
			{ width },
			{
				match: (n: any) => EMBED_TYPES.includes(n.type) && n.url === url,
			}
		);
	}, [isFullWidth, unit, percentSize, finalSize.width, editor, url]);

	const handleEditClick: MouseEventHandler<HTMLButtonElement> = useCallback(({ currentTarget }) => {
		editPopupRef?.current?.toggle(currentTarget);
	}, []);

	const handleSave = (data: EmbedEditFormData) => {
		const path = ReactEditor.findPath(editor, element);
		Transforms.setNodes<ImageElement | VideoElement>(editor, data, { at: path });
	};

	useEffect(() => {
		const path = ReactEditor.findPath(editor, element);
		Transforms.setNodes<ImageElement | VideoElement>(
			editor,
			{
				widthUnit: unit,
				float,
			},
			{ at: path }
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [element, float, unit]);

	return (
		<>
			<EmbedEditPopup ref={editPopupRef} element={element} onSave={handleSave} />
			<div
				{...attributes}
				className={classNames('embed', {
					'w-full': isFullWidth,
					'embed-edit': selected && focused,
					'float-left': float === 'left',
					'float-right': float === 'right',
				})}
				style={{ width: unit === '%' ? `${percentSize}%` : undefined }}
			>
				<div
					contentEditable={false}
					style={{ width: !isFullWidth && unit !== '%' ? `${size.width}px` : undefined }}
					className={classNames({ 'w-full': isFullWidth })}
				>
					{children}
					{selected && (
						<>
							<div className="tools tools-tl">
								<Button
									onClick={handleEditClick}
									icon="pencil"
									iconOnly
									rounded
									variant="primary"
									label="Edytuj"
								/>
							</div>
							<div className="tools tools-tr">
								<span className={classNames('info', { 'info-show': resizing })}>
									{unit === 'px' && `${size.width}x${size.height}px`}
									{unit === '%' && `${percentSize}%`}
									{isFullWidth && `100%`}
								</span>
							</div>
							<div className="tools tools-br">
								<ButtonGroup
									variant="on-purple"
									iconOnly
									buttons={[
										{
											key: 'floatLeft',
											label: 'Opływaj z lewej',
											icon: 'outdent',
											onClick: () => {
												setFloat(float === 'left' ? undefined : 'left');
											},
											active: float === 'left',
										},
										{
											key: 'floatRight',
											label: 'Opływaj z prawej',
											icon: 'indent',
											onClick: () => {
												setFloat(float === 'right' ? undefined : 'right');
											},
											active: float === 'right',
										},
									]}
								/>
								<ButtonGroup
									variant="on-purple"
									iconOnly
									buttons={[
										{
											key: 'px',
											label: 'Piksele',
											icon: 'th-large',
											iconStyle: 'solid',
											onClick: () => {
												setUnit('px');
											},
											active: unit === 'px',
										},
										{
											key: 'floatRight',
											label: 'Procenty',
											icon: 'percentage',
											iconStyle: 'regular',
											onClick: () => {
												setUnit('%');
											},
											active: unit === '%',
										},
										{
											key: 'fw',
											label: 'Rozciągnij na pełną szerokość',
											icon: 'arrows-left-right',
											onClick: () => {
												setIsFullWidth(!isFullWidth);
											},
											active: isFullWidth,
										},
									]}
								/>
								<Button
									onMouseDown={onMouseDown}
									icon="up-right-and-down-left-from-center"
									iconOnly
									rounded
									variant="on-purple"
									label="Zmień rozmiar"
									disabled={isFullWidth}
								/>
							</div>
						</>
					)}
				</div>
			</div>
		</>
	);
};
export default Image;
